<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import router from '@/router';
import ProgressFrame from '../layout/ProgressFrame.vue';
import HorizontalContentRow from '../../components/HorizontalContentRow.vue';
import ProjectForm from '../../components/ProjectForm.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import { useProjectStore } from '../../stores/projectStore';
import { ProjectFormModel } from '../../models/Project';

const props = defineProps<{
  projectId: number;
}>();

const projectStore = useProjectStore();
const projectId = computed(() => props.projectId);
const projectData = computed(() => projectStore.getProjectById(projectId.value) ?? null);
const projectForm = ref<ProjectFormModel>(new ProjectFormModel().deserialize(projectData.value));

watch(
  projectData,
  (data) => {
    projectForm.value = new ProjectFormModel().deserialize(data);
  },
  { immediate: true },
);

const projectFormRef = ref<InstanceType<typeof ProjectForm> | null>(null);

const goToNext = async () => {
  if (!projectFormRef.value?.validateInput()) {
    // Do not proceed if there are errors
    return;
  }

  try {
    await projectStore.updateProject(projectId.value, projectForm.value);
    router.push({ name: 'ProjectSelectSite', params: { projectId: projectId.value } });
  } catch (error: unknown) {
    message.error((error as Error).message);
    return;
  }
};

/**
 * If the user refreshes the page and the project value is not found
 * Navigate to the ProjectSelect page
 */
if (!projectData.value) {
  router.push({ name: 'ProjectSelect' });
}
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow>
    <template #title>Edit project</template>
    <div class="content">
      <HorizontalContentRow firstRow lastRow>
        <ProjectForm
          ref="projectFormRef"
          :project="projectForm"
          :showProjectName="true"
          :showPersistedSites="true"
          :deletePersistedSite="false"
          :editAddressPersistedSite="false"
        />
      </HorizontalContentRow>

      <div class="end-of-page">
        <ButtonPrimary block @click="goToNext" class="next-button" data-tn="next-btn">
          Next
          <template #iconRight>
            <ChevronRightSVG class="chevron-right-icon" />
          </template>
        </ButtonPrimary>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  margin: 0;
  padding: 0;
  .content-row {
    width: 100%;
  }
}

.end-of-page {
  padding: @content-side-margin;
  width: 100%;
}
</style>
